// The AC–Fan cycle editor — one compact page, every label on the same line as its
// control. Rules: core/roomCycle/roomCycle.js; engine: Home Automation api/new/roomCycle.js.
//
// ⚠️ ON A PHONE IT IS A SHEET, NEVER fullScreen (owner, 2026-10-26): the top of the
// screen holds the status bar, the bottom the navigation buttons — PHONE_SHEET
// leaves a gap at both, and the body scrolls between a fixed title and buttons.

import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
  IconButton, InputLabel, MenuItem, Select, Switch, TextField,
  ToggleButton, ToggleButtonGroup, Typography, useMediaQuery,
} from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import ToysIcon from '@mui/icons-material/Toys';
import WbTwilightIcon from '@mui/icons-material/WbTwilight';
import HistoryIcon from '@mui/icons-material/History';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  FAN_OFF, FAN_SPEEDS, MIN_OFF_FLOOR, PRESETS, autoPick, settingsProblem, targetKey, targetLabel, withDefaults,
} from '../../../core/roomCycle/roomCycle';
import { getRoomCycleLog } from '../../../core/api/api';
import { PHONE_SHEET, FIT_SM } from '../fitDialog';

function Stepper({
  value, onChange, min, max, step = 5, unit = 'min',
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <IconButton size="small" sx={{ p: 0.25 }} onClick={() => onChange(Math.max(min, value - step))} disabled={value <= min} aria-label="less">
        <RemoveIcon fontSize="small" />
      </IconButton>
      <Typography sx={{ minWidth: 52, textAlign: 'center', fontWeight: 700, fontSize: 15 }}>{`${value} ${unit}`}</Typography>
      <IconButton size="small" sx={{ p: 0.25 }} onClick={() => onChange(Math.min(max, value + step))} disabled={value >= max} aria-label="more">
        <AddIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

function Speeds({ value, onChange, withOff = false }) {
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      onChange={(e, v) => { if (v != null) onChange(v); }}
      sx={{ '& .MuiToggleButton-root': { px: 0.9, py: 0.25, fontSize: 12, textTransform: 'none' } }}
    >
      {withOff && <ToggleButton value={FAN_OFF}>Off</ToggleButton>}
      {FAN_SPEEDS.map((sp) => <ToggleButton key={sp} value={sp}>{sp}</ToggleButton>)}
    </ToggleButtonGroup>
  );
}

/** Label on the left, control on the right — one line, wrapping only when it must. */
function Row({ icon, title, children }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', columnGap: 1, rowGap: 0.25, flexWrap: 'wrap', py: 0.25,
    }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {icon}
        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{title}</Typography>
      </Box>
      {children}
    </Box>
  );
}

function Section({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'text.secondary', mt: 1, mb: 0.25,
    }}
    >
      {children}
    </Typography>
  );
}

function TargetSelect({
  label, list, value, onChange,
}) {
  const v = value ? targetKey(value) : '';
  return (
    <FormControl size="small" sx={{ minWidth: 0, flex: 1 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={list.some((o) => targetKey(o) === v) ? v : ''}
        onChange={(e) => {
          const o = list.find((x) => targetKey(x) === e.target.value);
          onChange(o ? { UniqueNumber: o.UniqueNumber, Output: o.Output } : null);
        }}
      >
        {list.map((o) => <MenuItem key={targetKey(o)} value={targetKey(o)}>{targetLabel(o)}</MenuItem>)}
      </Select>
    </FormControl>
  );
}

function CycleLog({ userId, roomId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    getRoomCycleLog(userId, roomId)
      .then((r) => { if (alive) setRows(r?.data?.log || []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [userId, roomId]);
  if (rows === null) return <Typography color="text.secondary">Loading…</Typography>;
  if (!rows.length) return <Typography color="text.secondary">Nothing yet.</Typography>;
  return (
    <Box>
      {rows.map((r, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Box key={i} sx={{ display: 'flex', gap: 1.5, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 92 }}>{r.At}</Typography>
          <Typography variant="body2">{r.Note}</Typography>
        </Box>
      ))}
    </Box>
  );
}

/**
 * props: open, onClose, data (GetRoomCycle reply), onSave(settings) -> '' | error,
 *        onSaveAndStart (optional, when not running), userId, roomId, roomName
 */
export default function RoomCycleDialog({
  open, onClose, data, onSave, onSaveAndStart, userId, roomId, roomName,
}) {
  const phone = useMediaQuery('(max-width:600px)');
  const [s, setS] = useState(withDefaults(null));
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const candidates = data?.candidates || { ac: [], fan: [] };
  const running = !!data?.state;

  useEffect(() => {
    if (!open) return;
    setS(autoPick(data?.settings, data?.candidates));
    setErr('');
    setBusy(false);
    setShowLog(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (patch) => { setS((p) => ({ ...p, ...patch })); setErr(''); };
  const setM = (patch) => { setS((p) => ({ ...p, Morning: { ...p.Morning, ...patch } })); setErr(''); };

  const submit = async (andStart) => {
    const p = settingsProblem(s);
    if (p) { setErr(p); return; }
    setBusy(true);
    const e = await (andStart && onSaveAndStart ? onSaveAndStart(s) : onSave(s));
    setBusy(false);
    if (e) setErr(e); else onClose();
  };

  const timeField = (value, onChange, disabled) => (
    <TextField
      type="time"
      size="small"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      sx={{ width: 112, '& input': { py: 0.6, fontSize: 14 } }}
    />
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} {...(phone ? PHONE_SHEET : FIT_SM)}>
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', gap: 1, py: 1, pr: 1,
      }}
      >
        {showLog && <IconButton size="small" edge="start" onClick={() => setShowLog(false)}><ArrowBackIcon /></IconButton>}
        <Box sx={{
          flex: 1, minWidth: 0, fontSize: 16, fontWeight: 700,
        }}
        >
          {showLog ? 'Cycle history' : 'AC–Fan cycle'}
          <Typography component="span" sx={{ fontSize: 13, color: 'text.secondary', ml: 1 }}>{roomName}</Typography>
        </Box>
        {!showLog && (
          <IconButton size="small" onClick={() => setShowLog(true)} aria-label="history"><HistoryIcon /></IconButton>
        )}
      </DialogTitle>
      <DialogContent dividers sx={{ py: 1, px: 1.5 }}>
        {showLog ? <CycleLog userId={userId} roomId={roomId} /> : (
          <>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {PRESETS.map((pr) => (
                <Chip
                  key={pr.key}
                  size="small"
                  label={`${pr.label} ${pr.AcMin}/${pr.FanMin}`}
                  onClick={() => set({ AcMin: pr.AcMin, FanMin: pr.FanMin, FanSpeed: pr.FanSpeed })}
                  color={s.AcMin === pr.AcMin && s.FanMin === pr.FanMin && s.FanSpeed === pr.FanSpeed ? 'primary' : 'default'}
                />
              ))}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <TargetSelect label="AC" list={candidates.ac} value={s.Ac} onChange={(v) => set({ Ac: v })} />
              <TargetSelect label="Fan" list={candidates.fan} value={s.Fan} onChange={(v) => set({ Fan: v })} />
            </Box>
            {(!candidates.ac.length || !candidates.fan.length) && (
              <Alert severity="info" sx={{ mt: 0.5, py: 0 }}>
                {`No ${!candidates.ac.length ? 'AC' : 'fan'} here — set that switch's "Connected to" to ${!candidates.ac.length ? 'AC' : 'Fan'}.`}
              </Alert>
            )}

            <Section>CYCLE</Section>
            <Row icon={<AcUnitIcon fontSize="small" color="primary" />} title="AC">
              <Stepper value={s.AcMin} min={5} max={240} onChange={(v) => set({ AcMin: v })} />
            </Row>
            <Row icon={<ToysIcon fontSize="small" color="primary" />} title="Fan">
              <Stepper value={s.FanMin} min={5} max={240} onChange={(v) => set({ FanMin: v })} />
            </Row>
            <Row title="Fan speed %">
              <Speeds value={s.FanSpeed} onChange={(v) => set({ FanSpeed: v })} />
            </Row>
            <Row title="Fan with AC">
              <Speeds withOff value={s.FanDuringAc} onChange={(v) => set({ FanDuringAc: v })} />
            </Row>
            <Row title="AC rests at least">
              <Stepper value={s.MinOffMin} min={MIN_OFF_FLOOR} max={30} step={1} onChange={(v) => set({ MinOffMin: v })} />
            </Row>

            <Section>HAND PRESS ON AC / FAN</Section>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={s.HandPress}
              onChange={(e, v) => { if (v) set({ HandPress: v }); }}
              sx={{
                '& .MuiToggleButton-root': {
                  py: 0.4, fontSize: 12, textTransform: 'none', lineHeight: 1.2,
                },
              }}
            >
              <ToggleButton value="keep">Keep cycling</ToggleButton>
              <ToggleButton value="stop">Stop — normal switches</ToggleButton>
            </ToggleButtonGroup>

            <Row icon={<WbTwilightIcon fontSize="small" color="primary" />} title="Morning plan">
              <Switch size="small" checked={s.Morning.On} onChange={(e) => setM({ On: e.target.checked })} />
            </Row>
            {s.Morning.On && (
              <>
                <Row title="Stop at">
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end',
                  }}
                  >
                    {timeField(s.Morning.StopAt, (v) => setM({ StopAt: v }))}
                    <Speeds withOff value={s.Morning.FanSpeed} onChange={(v) => setM({ FanSpeed: v })} />
                  </Box>
                </Row>
                {s.Morning.FanSpeed > FAN_OFF && (
                  <Row title="Fan off at">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Switch size="small" checked={s.Morning.FanOffOn} onChange={(e) => setM({ FanOffOn: e.target.checked })} />
                      {timeField(s.Morning.FanOffAt, (v) => setM({ FanOffAt: v }), !s.Morning.FanOffOn)}
                    </Box>
                  </Row>
                )}
              </>
            )}
            <Row title="Notify me">
              <Switch size="small" checked={s.Notify} onChange={(e) => set({ Notify: e.target.checked })} />
            </Row>

            {err && <Alert severity="error" sx={{ mt: 0.5, py: 0 }}>{err}</Alert>}
            {running && (
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
                Running — changes apply from the next step.
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      {!showLog && (
        <DialogActions sx={{ py: 0.5 }}>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={() => submit(false)} disabled={busy}>Save</Button>
          {!running && onSaveAndStart && (
            <Button variant="contained" onClick={() => submit(true)} disabled={busy}>Save &amp; start</Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}
